const PROFESSIONAL_SIGNATURE_MARKER = 'data-sitesift-professional-signature="v1"';
export const SIGNATURE_LOGO_MAX_BYTES = 48 * 1024;
const SIGNATURE_LOGO_MAX_DIMENSIONS = [240, 200, 160, 120];
const SIGNATURE_INFO_TABLE_WIDTH = 390;
const SIGNATURE_INFO_COLUMN_WIDTH = 180;
const PROFESSIONAL_FIELD_KEYS = [
  'name',
  'title',
  'team',
  'licenseLine',
  'phone',
  'email',
  'company',
  'website',
  'location',
  'linkedinUrl',
  'logoDataUrl'
];

function clean(value) {
  return (value || '').trim();
}

function escapeHtml(value) {
  return clean(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeUrl(value) {
  const url = clean(value);
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function displayWebsite(value) {
  return clean(value).replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

export function getSignatureOrganizationDefaults() {
  return {};
}

function safeImageDataUrl(value) {
  const dataUrl = clean(value);
  if (/^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i.test(dataUrl)) {
    return dataUrl;
  }
  return '';
}

export function estimateDataUrlDecodedBytes(value = '') {
  const dataUrl = safeImageDataUrl(value);
  if (!dataUrl) return 0;
  const base64 = dataUrl.split(',')[1] || '';
  const padding = (base64.match(/=+$/) || [''])[0].length;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

export function shrinkSignatureLogoDataUrl(originalDataUrl, resizedDataUrl, maxBytes = SIGNATURE_LOGO_MAX_BYTES) {
  const original = safeImageDataUrl(originalDataUrl);
  if (original && estimateDataUrlDecodedBytes(original) <= maxBytes) {
    return original;
  }

  const resized = safeImageDataUrl(resizedDataUrl);
  if (resized && estimateDataUrlDecodedBytes(resized) <= maxBytes) {
    return resized;
  }

  return '';
}

function renderImageToDataUrl(image, maxDimension) {
  const scale = Math.min(1, maxDimension / image.width, maxDimension / image.height);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return '';
  context.clearRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/png');
}

export function resizeSignatureLogoFile(file, maxBytes = SIGNATURE_LOGO_MAX_BYTES) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      reject(new Error('Please upload an image file for the signature logo.'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the signature logo.'));
    reader.onload = () => {
      const originalDataUrl = String(reader.result || '');
      if (shrinkSignatureLogoDataUrl(originalDataUrl, '', maxBytes)) {
        resolve(originalDataUrl);
        return;
      }

      const image = new Image();
      image.onerror = () => reject(new Error('Could not prepare the signature logo.'));
      image.onload = () => {
        let bestDataUrl = '';
        SIGNATURE_LOGO_MAX_DIMENSIONS.forEach(dimension => {
          const rendered = renderImageToDataUrl(image, dimension);
          if (!rendered) return;
          const shrunk = shrinkSignatureLogoDataUrl(originalDataUrl, rendered, maxBytes);
          if (shrunk && !bestDataUrl) {
            bestDataUrl = shrunk;
          }
        });

        if (bestDataUrl) {
          resolve(bestDataUrl);
        } else {
          reject(new Error('Please upload a simpler or smaller logo image.'));
        }
      };
      image.src = originalDataUrl;
    };
    reader.readAsDataURL(file);
  });
}

export function isProfessionalSignatureHtml(signature = '') {
  return clean(signature).includes(PROFESSIONAL_SIGNATURE_MARKER);
}

export function sanitizeProfessionalSignatureHtml(signature = '') {
  const html = clean(signature);
  if (!isProfessionalSignatureHtml(html)) return '';

  if (typeof DOMParser === 'undefined') {
    return html
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
      .replace(/javascript:/gi, '');
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, style, iframe, object, embed').forEach(node => node.remove());
  doc.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim();
      if (name.startsWith('on')) {
        node.removeAttribute(attr.name);
        return;
      }
      if ((name === 'href' || name === 'src') && /^javascript:/i.test(value)) {
        node.removeAttribute(attr.name);
        return;
      }
      if (name === 'src' && node.tagName.toLowerCase() === 'img') {
        const isAllowedImage = /^(data:image\/(png|jpe?g|gif|webp);base64,|cid:|https?:\/\/)/i.test(value);
        if (!isAllowedImage) node.removeAttribute(attr.name);
      }
    });
  });
  return doc.body.innerHTML;
}

export function buildProfessionalSignature(fields = {}) {
  const values = {
    name: clean(fields.name),
    title: clean(fields.title),
    team: clean(fields.team),
    licenseLine: clean(fields.licenseLine),
    phone: clean(fields.phone),
    email: clean(fields.email),
    company: clean(fields.company),
    website: clean(fields.website),
    location: clean(fields.location),
    linkedinUrl: clean(fields.linkedinUrl),
    logoDataUrl: safeImageDataUrl(fields.logoDataUrl)
  };

  const hasContent = Object.entries(values).some(([key, value]) => key !== 'logoDataUrl' && Boolean(value));
  if (!hasContent) return '';

  const websiteHref = normalizeUrl(values.website);
  const websiteText = displayWebsite(values.website);
  const linkedInHref = normalizeUrl(values.linkedinUrl);
  const logoAlt = escapeHtml(values.company ? `${values.company} logo` : `${values.name} logo`);

  const logoCell = values.logoDataUrl
    ? `<td valign="top" style="padding-right:30px;vertical-align:top;width:120px;"><img src="${values.logoDataUrl}" alt="${logoAlt}" style="max-width:120px;max-height:150px;width:auto;height:auto;object-fit:contain;display:block;border:0;" /></td>`
    : '';

  const titleLines = [
    values.title,
    values.team,
    values.licenseLine
  ].filter(Boolean).map(line => `<span style="font-size:10pt;color:#000000;">${escapeHtml(line)}</span><br>`).join('');

  const emailLine = values.email
    ? `<a href="mailto:${escapeHtml(values.email)}" style="color:#000000;text-decoration:underline;text-decoration-color:#CC0000;text-underline-offset:2px;">${escapeHtml(values.email)}</a><br>`
    : '';

  const linkedInLine = linkedInHref
    ? `<a href="${escapeHtml(linkedInHref)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;margin-top:4px;"><span aria-hidden="true" style="display:inline-block;width:20px;height:20px;line-height:20px;text-align:center;border-radius:50%;background:#6b7280;color:#ffffff;font-size:11px;font-weight:bold;font-family:Arial,Helvetica,sans-serif;">in</span></a>`
    : '';

  const websiteLine = websiteHref
    ? `<a href="${escapeHtml(websiteHref)}" target="_blank" rel="noopener noreferrer" style="color:#CC0000;text-decoration:underline;text-decoration-color:#CC0000;text-underline-offset:2px;">${escapeHtml(websiteText)}</a><br>`
    : '';

  return `<!-- sitesift:professional-signature:v1 -->
<div data-sitesift-professional-signature="v1" style="font-family:Arial,Helvetica,sans-serif;font-size:10pt;color:#000000;line-height:1.5;">
Best,<br>
<br>
<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-top:10px;font-family:Arial,Helvetica,sans-serif;font-size:10pt;color:#000000;">
<tr>
${logoCell}
<td valign="top" style="vertical-align:top;font-family:Arial,Helvetica,sans-serif;font-size:10pt;color:#000000;width:${SIGNATURE_INFO_TABLE_WIDTH}px;">
<table cellpadding="0" cellspacing="0" border="0" width="${SIGNATURE_INFO_TABLE_WIDTH}" style="border-collapse:collapse;width:${SIGNATURE_INFO_TABLE_WIDTH}px;max-width:${SIGNATURE_INFO_TABLE_WIDTH}px;">
<tr>
<td colspan="2" style="padding-bottom:8px;">
${values.name ? `<strong style="font-size:12pt;font-weight:bold;color:#000000;">${escapeHtml(values.name)}</strong><br>` : ''}
${titleLines}
</td>
</tr>
<tr>
<td colspan="2" style="padding:8px 0;">
<div style="border-top:1px solid #CC0000;width:100%;"></div>
</td>
</tr>
<tr>
<td valign="top" width="${SIGNATURE_INFO_COLUMN_WIDTH}" style="padding-right:30px;vertical-align:top;font-size:10pt;color:#000000;width:${SIGNATURE_INFO_COLUMN_WIDTH}px;">
${values.phone ? `${escapeHtml(values.phone)}<br>` : ''}
${emailLine}
${linkedInLine}
</td>
<td valign="top" width="${SIGNATURE_INFO_COLUMN_WIDTH}" style="vertical-align:top;font-size:10pt;color:#000000;width:${SIGNATURE_INFO_COLUMN_WIDTH}px;">
${values.company ? `<strong style="font-weight:bold;color:#000000;">${escapeHtml(values.company)}</strong><br>` : ''}
${websiteLine}
${values.location ? `<span style="color:#000000;">${escapeHtml(values.location)}</span>` : ''}
</td>
</tr>
</table>
</td>
</tr>
</table>
</div>`;
}

export function buildActiveSignatureFromProfile(profile = {}, organizationName = '') {
  const signatureMode = clean(profile.signatureMode);
  if (signatureMode === 'none' || !signatureMode) return '';

  if (signatureMode === 'professional') {
    const professionalSignature = profile.professionalSignature || {};
    const professionalFields = PROFESSIONAL_FIELD_KEYS.reduce((acc, key) => {
      acc[key] = professionalSignature[key] || '';
      return acc;
    }, {});
    const built = buildProfessionalSignature({
      ...professionalFields,
      name: professionalFields.name || profile.preferredDisplayName || profile.displayName || '',
      email: professionalFields.email || profile.email || '',
      company: professionalFields.company || organizationName || ''
    });
    if (built) return built;
    const cached = clean(profile.emailSignature);
    if (cached && isProfessionalSignatureHtml(cached)) return sanitizeProfessionalSignatureHtml(cached);
    return '';
  }

  if (signatureMode === 'custom') {
    return profile.emailSignature || '';
  }

  return '';
}

export function buildInitialSignatureProfileDefaults(email = '', displayName = '') {
  const organizationDefaults = getSignatureOrganizationDefaults(email);
  if (!organizationDefaults.organizationName) return {};

  const professionalSignature = {
    name: clean(displayName),
    title: '',
    team: '',
    licenseLine: '',
    phone: '',
    email: clean(email),
    company: organizationDefaults.professionalSignature.company || '',
    website: organizationDefaults.professionalSignature.website || '',
    location: '',
    linkedinUrl: organizationDefaults.professionalSignature.linkedinUrl || '',
    logoDataUrl: organizationDefaults.professionalSignature.logoDataUrl || ''
  };

  return {
    organizationName: organizationDefaults.organizationName,
    signatureMode: 'professional',
    professionalSignature,
    emailSignature: buildProfessionalSignature(professionalSignature)
  };
}
